import DOMPurify from 'dompurify';
import { Model } from 'survey-core';

/** Allow web/contact navigation without permitting script URLs, credentials or control characters. */
export function safeHref(value) {
  const hasControls = typeof value === 'string' && [...value].some(character => {
    const code = character.charCodeAt(0);
    return code < 32 || code === 127;
  });

  if (typeof value !== 'string' || hasControls) {
    return undefined;
  }

  try {
    const url = new URL(value, window.location.origin);

    if (!['http:', 'https:', 'mailto:', 'tel:'].includes(url.protocol) || url.username || url.password) {
      return undefined;
    }

    return url.href;
  } catch {
    return undefined;
  }
}

/** Permit same-origin object URLs for CSV downloads created by the application. */
export function safeDownloadHref(value) {
  if (typeof value === 'string') {
    try {
      const url = new URL(value);

      if (url.protocol === 'blob:' && url.origin === window.location.origin) {
        return url.href;
      }
    } catch {
      // Normal relative links are checked by the navigation policy below.
    }
  }

  return safeHref(value);
}

/** Escape messages because the toast library inserts its message using innerHTML. */
export function toastText(value) {
  return String(value ?? '').replace(/[&<>"']/g, character => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
  })[character]);
}

/** Keep form instructions readable without scripts, embedded resources or active controls. */
export function sanitizeFormHtml(value) {
  return DOMPurify.sanitize(String(value ?? ''), {
    ALLOWED_TAGS: [
      'p', 'div', 'span', 'br', 'hr', 'strong', 'b', 'em', 'i', 'u', 's',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'blockquote',
      'table', 'thead', 'tbody', 'tr', 'th', 'td', 'a', 'sup', 'sub'
    ],
    ALLOWED_ATTR: ['href', 'title', 'colspan', 'rowspan'],
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false
  });
}

/** Sanitize the final HTML, after SurveyJS substitutes answer values into form templates. */
export class SafeSurveyModel extends Model {
  processHtml(html, reason) {
    return sanitizeFormHtml(super.processHtml(html, reason));
  }

  /** Apply the same boundary if a form registers a Markdown conversion callback. */
  getSurveyMarkdownHtml(...args) {
    const html = super.getSurveyMarkdownHtml(...args);

    return html == null ? html : sanitizeFormHtml(html);
  }
}
